<template>
  <div id="coworking-container">
    <div id="coworking-header">
      <h2 id="title" class="mt-5" v-if="!isEdit">
        {{ $t("utils.spaces.space-info.title") }}
      </h2>
      <h2 id="title" class="mt-5" v-else>
        {{ $t("coworking-info.coworking-form.title") }}
      </h2>
    </div>
    <div id="coworking-info" class="mt-4" v-if="!isEdit">
      <div class="d-inline">
        <div class="row">
          <div class="col">
            <h2>{{ $t("utils.spaces.space-info.space-name") }}</h2>
            <p>{{ info.coworkingName }}</p>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col">
            <h2>{{ $t("coworking-info.coworking-form.city-placeholder") }}</h2>
            <p>{{ info.city }}</p>
          </div>
          <div class="col">
            <h2>
              {{ $t("coworking-info.coworking-form.address-placeholder") }}
            </h2>
            <p>{{ info.address }}</p>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col">
            <h2>
              {{ $t("coworking-info.coworking-form.website-placeholder") }}
            </h2>
            <p>{{ info.website }}</p>
          </div>
          <div class="col">
            <h2>{{ $t("coworking-info.coworking-form.phone-placeholder") }}</h2>
            <p>{{ info.phone }}</p>
          </div>
        </div>
      </div>
    </div>

    <div id="coworking-form" class="mt-4" v-else>
      <b-form-group class="d-inline">
        <div class="row">
          <b-form-input
            v-model="info.coworkingName"
            required
            :placeholder="$t('utils.spaces.space-info.space-name') + '*'"
            class="form col"
          />
          <b-form-input
            v-model="info.website"
            required
            :placeholder="
              $t('coworking-info.coworking-form.website-placeholder') + '*'
            "
            class="form col form-margin"
          />
        </div>
        <div class="row mt-4">
          <b-form-input
            required
            class="form col"
            :placeholder="
              $t('coworking-info.coworking-form.address-placeholder') + '*'
            "
            @keyup="getPlaces()"
            v-model="info.location"
          />
        </div>
        <div class="row mt-2">
          <div class="col">
            <ul id="places-list">
              <li
                v-for="(result, index) in searchResults"
                :key="index"
                @click="selectPlace(result)"
              >
                {{ result.description }}
              </li>
            </ul>
          </div>
        </div>
        <div class="row phone-box mt-4">
          <label>{{
            $t("coworking-info.coworking-form.phone-placeholder")
          }}</label>
          <b-form-input v-model="info.phone" placeholder="+00 000000000" />
        </div>
      </b-form-group>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: ["info", "isEdit"],
  data() {
    return {
      searchResults: [],
      service: null,
      place: null,
      locationDetails: {}
    };
  },
  metaInfo() {
    return {
      script: [
        {
          src: `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_FIREBASE_API_KEY}&libraries=places`,
          callback: () => this.mapsInit()
        }
      ]
    };
  },
  methods: {
    mapsInit() {
      this.service = new window.google.maps.places.AutocompleteService();
    },

    getPlaces() {
      if (this.$props.info.location.trim() !== "") {
        this.service.getPlacePredictions(
          {
            input: this.$props.info.location,
            types: ["address"]
          },
          this.displaySuggestions
        );
      } else {
        this.searchResults = [];
      }
    },

    displaySuggestions(predictions, status) {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
        this.searchResults = [];
        return;
      }
      this.searchResults = predictions;
    },

    async selectPlace(place) {
      const { data } = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${place.place_id}&key=${process.env.VUE_APP_FIREBASE_API_KEY}`
      );
      place = data.results[0];
      this.place = place;
      this.searchResults = [];
      this.$props.info.location = place.formatted_address;

      const zip = this.place.address_components.filter(type =>
        type.types.includes("postal_code")
      );

      let city = [];

      if (this.place.address_components.length < 7) {
        city = this.place.address_components.filter(type =>
        type.types.includes("locality"))
        } else {
          city = this.place.address_components.filter(type =>
           type.types.includes("administrative_area_level_3"));
        }

      const country = this.place.address_components.filter(type =>
        type.types.includes("country")
      );

      this.$props.info.locationDetails = {
        address: this.place.formatted_address,
        coordinates: this.place.geometry.location,
        city: city.length ? city[0].long_name : null,
        country: country.length ? country[0].long_name : null,
        zipcode: zip.length ? zip[0].long_name : null
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/fonts";
@import "src/assets/scss/forms";

#coworking-container {
  .row {
    --bs-gutter-x: 0;
  }

  ::placeholder {
    color: #777777 !important;
  }

  #title {
    font-family: $worksans;
    font-style: normal;
    font-weight: 600;
    font-size: 24.22px;
    line-height: 38px;
  }

  #coworking-info {
    width: 750px;
    margin-left: 0;

    h2 {
      font-family: $inter;
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 19px;
      color: #767676;
    }

    p {
      font-family: $inter;
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 19px;
      color: #000000;
    }
  }

  .phone-box {
    padding-left: 20px;
    width: 335px;
    height: 56px;
    border: 1.6px solid #000000;
    box-sizing: border-box;
    border-radius: 6px;

    &:hover {
      box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 8px #7e4ee2;
    }

    label {
      padding-top: 5px;
      margin-left: -14px;
      font-family: $inter;
      font-style: normal;
      font-weight: normal;
      font-size: 11px;
      line-height: 13px;
      color: #000000;
    }

    input {
      width: 280px;
      height: 22px;
      border: none;
      background-color: #e5e5e5;
      padding-bottom: 5px;
      padding-top: 0;
      margin-top: 8px;
      margin-left: -16px;
      font-family: $inter;
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 19px;

      &:focus {
        box-shadow: none;
      }
    }
  }

  #places-list {
    margin-top: 10px;
    padding: 0;
    list-style: none;

    li {
      margin-bottom: 10px;
      cursor: pointer;
      font-family: $inter;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/info/components/Space.vue